import type { SVGProps } from "react";

/**
 * The icon set.
 *
 * Hand-drawn on a 24px grid rather than pulled from a library: an icon pack
 * is 40–60 kB for the six glyphs this site uses, and the six can be tuned to
 * one stroke weight so they read as a set instead of as six downloads.
 *
 * All of them are `aria-hidden` at the point of use and paired with real text
 * or an `sr-only` label. An icon is never the only carrier of meaning — an
 * envelope means "email" only to someone who already knows.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="m3.5 7 7.2 5.3a2.2 2.2 0 0 0 2.6 0L20.5 7" />
    </Icon>
  );
}

export function LinkedInIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="2.5" width="19" height="19" rx="3.5" />
      <path d="M7 10.5v7" />
      <circle cx="7" cy="7.2" r="0.9" fill="currentColor" stroke="none" />
      <path d="M11.2 17.5v-7m0 2.2a2.6 2.6 0 0 1 5.2 0v4.8" />
    </Icon>
  );
}

export function GitHubIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.2 20.5c-4 1.2-4-2.1-5.7-2.5m11.4 4.5v-3.4a3 3 0 0 0-.8-2.3c2.7-.3 5.5-1.3 5.5-6a4.7 4.7 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.3s-1-.3-3.4 1.3a11.6 11.6 0 0 0-6 0C6.4 4 5.4 4.3 5.4 4.3a4.3 4.3 0 0 0-.1 3.3A4.7 4.7 0 0 0 4 10.8c0 4.7 2.8 5.7 5.5 6a3 3 0 0 0-.8 2.3v3.4" />
    </Icon>
  );
}

export function WhatsAppIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 20.5l1.3-4a8.2 8.2 0 1 1 3.1 3.03L3.5 20.5Z" />
      <path d="M9.1 8.2c.2-.5.4-.5.7-.5h.5c.2 0 .4 0 .6.5l.7 1.7c.1.3 0 .5-.1.7l-.4.5c-.2.2-.3.4-.1.7a6 6 0 0 0 2.8 2.4c.3.1.5 0 .7-.2l.5-.6c.2-.2.4-.2.6-.1l1.7.8c.2.1.4.2.4.4a2 2 0 0 1-1.4 1.7c-.6.2-1.4.2-3.6-.9a9 9 0 0 1-3.7-3.9c-.6-1.3-.5-2.3.1-3.2Z" />
    </Icon>
  );
}

export function LocationIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 21.5s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10.2" r="2.6" />
    </Icon>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3.5v11m0 0 4-4m-4 4-4-4" />
      <path d="M4.5 16.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2" />
    </Icon>
  );
}

export function ArrowIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14m-6-6 6 6-6 6" />
    </Icon>
  );
}
